import { z } from 'zod';

/** Направление профиля. Бизнес — база, остальные наследуют пустые поля. */
export const ProfileFacetSlug = z.enum(['business', 'sport', 'events', 'dating']);
export type ProfileFacetSlug = z.infer<typeof ProfileFacetSlug>;

export const PROFILE_FACET_SLUGS = ProfileFacetSlug.options;
export const PROFILE_FACET_ABOUT_MAX = 280;
export const PROFILE_FACET_TAGS_MAX = 10;

/** Виды активности. Свой тег нормализуется так же, как тег бизнес-карты. */
export const SPORT_ACTIVITY_TAGS = [
  'зал',
  'бег',
  'фитнес',
  'тренажерный_зал',
  'кроссфит',
  'плавание',
  'йога',
  'велосипед',
  'воркаут',
  'растяжка',
  'единоборства',
  'бокс',
  'тяжелая_атлетика',
  'кардио',
  'пилатес',
  'футбол',
  'баскетбол',
  'теннис',
  'скалолазание',
  'триатлон',
  'скейт',
  'сноуборд',
  'лыжи',
  'серфинг',
  'mma',
  'борьба',
] as const;

/** Темы и форматы мероприятий. */
export const EVENT_THEME_TAGS = [
  'мастермайнд',
  'нетворкинг',
  'бизнес_завтрак',
  'конференция',
  'митап',
  'хакатон',
  'пикник',
  'вечеринка',
  'инвест_клуб',
  'настольные_игры',
  'лекция',
  'мастер_класс',
  'питч_сессия',
  'панельная_дискуссия',
  'воркшоп',
  'вебинар',
  'клуб_по_интересам',
  'деловой_ужин',
  'выставка',
  'фестиваль',
  'standup',
  'квиз',
] as const;

export const FACET_ACTIVITY_CATALOG = {
  sport: SPORT_ACTIVITY_TAGS,
  events: EVENT_THEME_TAGS,
} as const;

export const CARD_INVITE_INTENTS = ['str', 'cmp', 'syn', 'tog', 'ai'] as const;
export const CardInviteIntent = z.enum(CARD_INVITE_INTENTS);
export type CardInviteIntent = z.infer<typeof CardInviteIntent>;

export const BUSINESS_SKILL_SLUGS = [
  'sales',
  'marketing',
  'product',
  'engineering',
  'design',
  'finance',
  'leadership',
  'negotiation',
  'speaking',
  'mentoring',
] as const;

export const SPORT_TAG_SLUGS = [
  'running',
  'gym',
  'yoga',
  'football',
  'tennis',
  'swimming',
  'cycling',
  'martial-arts',
  'volleyball',
  'padel',
] as const;

export const EVENT_FORMAT_SLUGS = [
  'networking',
  'mafia',
  'mastermind',
  'workshop',
  'conference',
  'breakfast',
  'board-games',
  'party',
] as const;

export const DATING_TAG_SLUGS = [
  'friends',
  'dates',
  'travel',
  'coffee',
  'walks',
  'culture',
  'conversation',
  'sport-together',
] as const;

const TAGS_BY_KIND: Record<ProfileFacetSlug, readonly string[]> = {
  business: BUSINESS_SKILL_SLUGS,
  sport: SPORT_TAG_SLUGS,
  events: EVENT_FORMAT_SLUGS,
  dating: DATING_TAG_SLUGS,
};

export function facetTagSlugs(kind: ProfileFacetSlug): readonly string[] {
  return TAGS_BY_KIND[kind];
}

export function isFacetTag(kind: ProfileFacetSlug, slug: string): boolean {
  return TAGS_BY_KIND[kind].includes(slug);
}

const LABELS: Record<ProfileFacetSlug, Record<string, { ru: string; en: string }>> = {
  business: {
    sales: { ru: 'Продажи', en: 'Sales' },
    marketing: { ru: 'Маркетинг', en: 'Marketing' },
    product: { ru: 'Продукт', en: 'Product' },
    engineering: { ru: 'Разработка', en: 'Engineering' },
    design: { ru: 'Дизайн', en: 'Design' },
    finance: { ru: 'Финансы', en: 'Finance' },
    leadership: { ru: 'Управление', en: 'Leadership' },
    negotiation: { ru: 'Переговоры', en: 'Negotiation' },
    speaking: { ru: 'Выступления', en: 'Speaking' },
    mentoring: { ru: 'Наставничество', en: 'Mentoring' },
  },
  sport: {
    running: { ru: 'Бег', en: 'Running' },
    gym: { ru: 'Зал', en: 'Gym' },
    yoga: { ru: 'Йога', en: 'Yoga' },
    football: { ru: 'Футбол', en: 'Football' },
    tennis: { ru: 'Теннис', en: 'Tennis' },
    swimming: { ru: 'Плавание', en: 'Swimming' },
    cycling: { ru: 'Велосипед', en: 'Cycling' },
    'martial-arts': { ru: 'Единоборства', en: 'Martial arts' },
    volleyball: { ru: 'Волейбол', en: 'Volleyball' },
    padel: { ru: 'Падел', en: 'Padel' },
  },
  events: {
    networking: { ru: 'Нетворкинг', en: 'Networking' },
    mafia: { ru: 'Мафия', en: 'Mafia' },
    mastermind: { ru: 'Мастермайнд', en: 'Mastermind' },
    workshop: { ru: 'Мастерская', en: 'Workshop' },
    conference: { ru: 'Конференция', en: 'Conference' },
    breakfast: { ru: 'Завтрак', en: 'Breakfast' },
    'board-games': { ru: 'Настолки', en: 'Board games' },
    party: { ru: 'Вечеринка', en: 'Party' },
  },
  dating: {
    friends: { ru: 'Друзья', en: 'Friends' },
    dates: { ru: 'Свидания', en: 'Dates' },
    travel: { ru: 'Путешествия', en: 'Travel' },
    coffee: { ru: 'Кофе', en: 'Coffee' },
    walks: { ru: 'Прогулки', en: 'Walks' },
    culture: { ru: 'Культура', en: 'Culture' },
    conversation: { ru: 'Разговоры', en: 'Conversation' },
    'sport-together': { ru: 'Спорт вместе', en: 'Sport together' },
  },
};

export function facetTagLabel(kind: ProfileFacetSlug, slug: string, locale: 'ru' | 'en'): string {
  const row = LABELS[kind][slug];
  if (!row) return slug;
  return locale === 'en' ? row.en : row.ru;
}

export const ProfileFacetStoredDto = z.object({
  kind: ProfileFacetSlug,
  about: z.string().nullable(),
  tags: z.array(z.string()),
  tagsOverridden: z.boolean(),
  /** Спорт и мероприятия: false прячет заполненный блок с визитки. */
  showOnCard: z.boolean(),
});
export type ProfileFacetStoredDto = z.infer<typeof ProfileFacetStoredDto>;

export const ProfileFacetResolvedDto = z.object({
  kind: ProfileFacetSlug,
  about: z.string(),
  tags: z.array(z.string()),
  aboutInherited: z.boolean(),
  tagsInherited: z.boolean(),
});
export type ProfileFacetResolvedDto = z.infer<typeof ProfileFacetResolvedDto>;

export const MeProfileFacetsDto = z.object({
  stored: z.array(ProfileFacetStoredDto),
  resolved: z.array(ProfileFacetResolvedDto),
});
export type MeProfileFacetsDto = z.infer<typeof MeProfileFacetsDto>;

export const UpdateProfileFacetInput = z
  .object({
    about: z.string().trim().max(PROFILE_FACET_ABOUT_MAX).nullable().optional(),
    tags: z.array(z.string().max(64)).max(PROFILE_FACET_TAGS_MAX).optional(),
    tagsOverridden: z.boolean().optional(),
    showOnCard: z.boolean().optional(),
  })
  .refine(
    (value) =>
      value.about !== undefined ||
      value.tags !== undefined ||
      value.tagsOverridden !== undefined ||
      value.showOnCard !== undefined,
    { message: 'empty patch' },
  );
export type UpdateProfileFacetInput = z.infer<typeof UpdateProfileFacetInput>;

export const FacetTagSuggestionQuery = z.object({
  kind: z.enum(['sport', 'events']),
  q: z.string().trim().min(1).max(40),
});
export type FacetTagSuggestionQuery = z.infer<typeof FacetTagSuggestionQuery>;
